import React from 'react';
import { Phone, Loader2, Building2, UserRound } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

/**
 * Ouverture d'un dossier depuis le coup de téléphone.
 *
 * LE STRICT MINIMUM, et c'est délibéré. Emeni (2026-08-26) : « on va avoir un client au téléphone pour une
 * demande de privatisation, on va prendre ses informations de base — nom, prénom, adresse email, numéro de
 * téléphone, pro ou privé ». Ni date, ni nombre de couverts : le client ne les a pas encore données, elles
 * se récoltent au rendez-vous et dans les échanges qui suivent.
 *
 * Demander une date ici obligerait le commercial à inventer une valeur pour passer l'écran — et une date
 * inventée finit toujours par être prise pour vraie.
 */
interface ContactCrm {
    id: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    email?: string;
    phone?: string;
}

export function NouveauDossierModal({ onClose, onCree }: { onClose: () => void; onCree: (id: string) => void }) {
    const [type, setType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [form, setForm] = React.useState({
        first_name: '', last_name: '', company_name: '', organizer_name: '', organizer_email: '', organizer_phone: '',
        email: '', phone: '',
    });
    const [saving, setSaving] = React.useState(false);
    const [erreur, setErreur] = React.useState('');

    // ── Reconnaître un client déjà connu ──────────────────────────────────────────────────────────
    // Demande d'Emeni (2026-08-29) : « si un client CRM est déjà existant il doit l'afficher lorsque les
    // premières lettres sont saisies, ça évite de réécrire les informations ». Le CRM porte déjà tout —
    // e-mail, téléphone, société — et les ressaisir au téléphone fait perdre du temps et laisse des
    // coquilles. Le serveur rattache déjà le dossier au contact dont l'e-mail correspond : préremplir
    // depuis la fiche existante, c'est donc aussi garantir ce rattachement plutôt que de le jouer sur une
    // adresse retapée de mémoire.
    const [suggestions, setSuggestions] = React.useState<ContactCrm[]>([]);
    const [chercheContacts, setChercheContacts] = React.useState(false);
    const [contactChoisi, setContactChoisi] = React.useState<string>('');
    // Le terme cherché : la société pour un professionnel ; pour un particulier, le nom s'il est saisi,
    // sinon le prénom. Je n'écoutais d'abord que le champ NOM, alors que PRÉNOM est le premier de la
    // rangée et donc celui où l'on tape spontanément : taper « iamani » là ne déclenchait rien du tout
    // (constaté par Emeni, 2026-08-29).
    const saisieNom = type === 'PROFESSIONNEL' ? form.company_name : (form.last_name.trim() || form.first_name);

    React.useEffect(() => {
        // Deux lettres avant de chercher : sur une seule, tout le carnet remonte et la liste n'aide plus.
        const terme = saisieNom.trim();
        // Sortie anticipée : on éteint aussi l'indicateur, sinon « Recherche dans le CRM... » restait
        // affiché indéfiniment dès qu'on effaçait le champ sous deux lettres.
        if (contactChoisi || terme.length < 2) { setSuggestions([]); setChercheContacts(false); return; }
        // Anti-rebond : on interroge le serveur quand la frappe s'arrête, pas à chaque touche.
        let annule = false;
        const t = setTimeout(async () => {
            setChercheContacts(true);
            try {
                const r: any = await moduleApi.searchCRMContacts(terme);
                if (!annule) setSuggestions((Array.isArray(r) ? r : []).slice(0, 5));
            } catch {
                if (!annule) setSuggestions([]);
            } finally {
                if (!annule) setChercheContacts(false);
            }
        }, 300);
        return () => { annule = true; clearTimeout(t); };
    }, [saisieNom, contactChoisi, type]);

    const libelleContact = (c: ContactCrm) =>
        c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Sans nom';

    const choisirContact = (c: ContactCrm) => {
        setForm({
            first_name: c.first_name || '',
            last_name: c.last_name || '',
            company_name: c.company_name || '',
            organizer_name: c.company_name ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '',
            // La recherche CRM ne renvoie pas les coordonnées directes du référent : on repart de zéro
            // plutôt que de laisser celles d'un contact précédemment sélectionné.
            organizer_email: '',
            organizer_phone: '',
            email: c.email || '',
            phone: c.phone || '',
        });
        setContactChoisi(c.id);
        setSuggestions([]);
    };

    // Une liste déroulante sous le champ, jamais un remplacement automatique de la saisie : c'est le
    // commercial qui reconnaît son client, pas le logiciel qui décide à sa place.
    const rechercheAboutie = !chercheContacts && suggestions.length === 0 && !contactChoisi && saisieNom.trim().length >= 2;
    const listeSuggestions = (suggestions.length > 0 || chercheContacts || rechercheAboutie) ? (
        <div className="rounded-lg border border-border bg-surface overflow-hidden">
            {chercheContacts && suggestions.length === 0 ? (
                <p className="px-2.5 py-1.5 text-[11px] text-content-muted">Recherche dans le CRM...</p>
            ) : rechercheAboutie ? (
                <p className="px-2.5 py-1.5 text-[11px] text-content-muted">Aucun client connu à ce nom — le dossier en créera un.</p>
            ) : suggestions.map((c) => (
                <button key={c.id} type="button" onClick={() => choisirContact(c)}
                    className="w-full text-left px-2.5 py-1.5 hover:bg-surface-soft transition-colors border-b border-border last:border-0">
                    <span className="block text-xs font-bold text-content">{libelleContact(c)}</span>
                    <span className="block text-[10px] text-content-muted">
                        {[c.email, c.phone].filter(Boolean).join(' · ') || 'Aucun contact renseigné'}
                    </span>
                </button>
            ))}
        </div>
    ) : null;

    const modifier = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }));
    // Retoucher le nom apres coup annule le choix : on ne veut pas garder une fiche selectionnee dont le
    // libelle ne correspond plus a ce qui est ecrit a l'ecran.
    const modifierNom = (patch: Partial<typeof form>) => { setContactChoisi(''); setForm((prev) => ({ ...prev, ...patch })); };
    const nomManquant = type === 'PROFESSIONNEL' ? !form.company_name.trim() : !form.last_name.trim();
    const bloque = nomManquant || !form.email.trim() || saving;

    const enregistrer = async () => {
        setSaving(true);
        setErreur('');
        try {
            const r: any = await moduleApi.createDossierPrivatisation({ ...form, type });
            onCree(r.dossier_id);
        } catch (e: any) {
            setErreur(e?.message || "Le dossier n'a pas pu être ouvert.");
        } finally {
            setSaving(false);
        }
    };

    const champ = 'w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500';

    return (
        <Modal open onClose={onClose} title="Nouvelle demande de privatisation" icon={<Phone size={16} />} maxWidth="max-w-md"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>Annuler</Button>
                    <Button variant="primary" onClick={enregistrer} disabled={bloque}>
                        {saving ? <Loader2 size={13} className="animate-spin" /> : null} Ouvrir le dossier
                    </Button>
                </>
            }>
            <div className="space-y-3">
                <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border">
                    Notez seulement ce que le client vient de vous donner au téléphone. La date, le nombre de
                    couverts et le déroulé se récolteront au rendez-vous — la privatisation n'entrera dans
                    Événementiel qu'à la signature du devis.
                </p>

                <div className="flex gap-2">
                    {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                        <button key={t} type="button" onClick={() => setType(t)}
                            className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${type === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content-muted hover:text-content'}`}>
                            {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                        </button>
                    ))}
                </div>

                {type === 'PROFESSIONNEL' ? (
                    <>
                        {/* Deux blocs, et l'un n'est pas l'autre : la société signe le devis, le référent
                            décroche le téléphone. Les mélanger faisait qu'on appelait un standard en
                            demandant « la personne qui organise » (demande d'Emeni, 2026-08-30). */}
                        <div className="space-y-2 p-3 rounded-xl border border-border bg-surface-soft">
                            <p className="text-[10px] font-black text-content uppercase tracking-widest flex items-center gap-1.5">
                                <Building2 size={12} /> Entreprise
                            </p>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Raison sociale *</label>
                                <input value={form.company_name} onChange={(e) => modifierNom({ company_name: e.target.value })} className={champ} autoFocus />
                                {listeSuggestions}
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email *</label>
                                <input type="email" value={form.email} onChange={(e) => modifier({ email: e.target.value })} className={champ} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                                <input value={form.phone} onChange={(e) => modifier({ phone: e.target.value })} className={champ} />
                            </div>
                        </div>

                        <div className="space-y-2 p-3 rounded-xl border border-border bg-surface-soft">
                            <p className="text-[10px] font-black text-content uppercase tracking-widest flex items-center gap-1.5">
                                <UserRound size={12} /> Référent / organisateur
                            </p>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom du référent</label>
                                <input value={form.organizer_name} onChange={(e) => modifier({ organizer_name: e.target.value })} className={champ} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email direct</label>
                                    <input type="email" value={form.organizer_email} onChange={(e) => modifier({ organizer_email: e.target.value })} className={champ} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone direct</label>
                                    <input value={form.organizer_phone} onChange={(e) => modifier({ organizer_phone: e.target.value })} className={champ} />
                                </div>
                            </div>
                            <p className="text-[10px] text-content-muted leading-relaxed">
                                Facultatif. Si le référent a une adresse directe, c'est à lui que partiront la
                                plaquette et le devis — sinon, ils partent à l'adresse de l'entreprise.
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Un particulier est son propre référent : lui demander « qui organise » n'apporterait
                            rien, le formulaire reste court (décision d'Emeni, 2026-08-30). */}
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prénom</label>
                                <input value={form.first_name} onChange={(e) => modifier({ first_name: e.target.value })} className={champ} autoFocus />
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom *</label>
                                <input value={form.last_name} onChange={(e) => modifierNom({ last_name: e.target.value })} className={champ} />
                            </div>
                            <div className="col-span-2">{listeSuggestions}</div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email *</label>
                            <input type="email" value={form.email} onChange={(e) => modifier({ email: e.target.value })} className={champ} />
                            <p className="text-[10px] text-content-muted">C'est par cette adresse que partira la plaquette.</p>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                            <input value={form.phone} onChange={(e) => modifier({ phone: e.target.value })} className={champ} />
                        </div>
                    </>
                )}

                {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
            </div>
        </Modal>
    );
}
